import type {
  ClientDetailsResponse,
  CreateClientDetailsDto,
  RiskAssessment,
  SaveDraftClientDetailsDto,
  StartAssessmentResponse,
} from '@nestjs-boilerplate/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { toast } from 'sonner';

import { apiClient } from '@/lib/api-client';
import { getAuthState } from '@/stores/auth.store';

import { riskAssessmentKeys } from '../../dashboard/api/risk-assessments';

// ============================================================================
// API Functions
// ============================================================================

export async function startAssessment(): Promise<StartAssessmentResponse> {
  return apiClient.fetch<StartAssessmentResponse>('/risk-assessments/start', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function getClientDetails(
  assessmentId: string,
): Promise<ClientDetailsResponse | null> {
  return apiClient.fetch<ClientDetailsResponse | null>(
    `/risk-assessments/${assessmentId}/client-details`,
  );
}

export async function saveClientDetails(
  assessmentId: string,
  data: CreateClientDetailsDto,
): Promise<ClientDetailsResponse> {
  return apiClient.fetch<ClientDetailsResponse>(
    `/risk-assessments/${assessmentId}/client-details`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

export async function saveDraftClientDetails(
  assessmentId: string,
  data: SaveDraftClientDetailsDto,
): Promise<ClientDetailsResponse> {
  return apiClient.fetch<ClientDetailsResponse>(
    `/risk-assessments/${assessmentId}/client-details/draft`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
}

export async function getRiskAssessment(id: string): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${id}`);
}

export async function runScreening(
  assessmentId: string,
  enabledChecks?: string[],
  adverseMediaMode: 'light' | 'dde' = 'light',
): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${assessmentId}/screen`, {
    method: 'POST',
    body: JSON.stringify({ enabledChecks, adverseMediaMode }),
  });
}

export async function upgradeToEdd(
  assessmentId: string,
  transactionId: string,
): Promise<{ success: boolean; jobId: string; assessmentId: string; optimalKycRef: string }> {
  // The upgrade clones the assessment into a new dated record (preserving the
  // original) and returns the new record's id/ref. The server writes the EDD
  // paid marker onto the new record using transactionId, so no separate
  // record-paid-service call is needed for the upgrade.
  return apiClient.fetch<{
    success: boolean;
    jobId: string;
    assessmentId: string;
    optimalKycRef: string;
  }>(`/risk-assessments/${assessmentId}/upgrade-to-edd`, {
    method: 'POST',
    body: JSON.stringify({ transactionId, createNewRecord: true }),
  });
}

export async function recordPaidService(
  assessmentId: string,
  serviceKey: string,
  transactionId: string,
): Promise<void> {
  await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
    method: 'POST',
    body: JSON.stringify({ serviceKey, transactionId }),
  });
}

/**
 * Basic report response for users who have paid for initial screening
 * but not the full report. Shows check summaries without detailed findings.
 */
export interface BasicReportResponse {
  assessmentId: string;
  clientDetails: {
    clientRef: string;
    fullName: string;
    dateOfBirth: string | null;
    nationality: string;
    occupation: string | null;
  };
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  enabledChecks: string[];
  checkStatuses: Array<{
    checkId: string;
    name: string;
    status: 'clear' | 'match' | 'error';
  }>;
  riskScoreBreakdown: {
    final_score: number;
    sanctions: number;
    adverse_media: number;
    fatf: number;
    companies_house: number;
    pep: number;
    cpi: number;
  } | null;
  dateGenerated: string | null;
  hasFullReportAccess: boolean;
}

export async function getBasicReport(assessmentId: string): Promise<BasicReportResponse> {
  return apiClient.fetch<BasicReportResponse>(`/risk-assessments/${assessmentId}/basic-report`);
}

/**
 * Screening status response for polling during adverse media processing
 */
export interface ScreeningStatusResponse {
  assessmentId: string;
  status:
    | 'NOT_STARTED'
    | 'FAST_CHECKS_DONE'
    | 'PROCESSING'
    | 'GENERATING_REPORT'
    | 'COMPLETED'
    | 'FAILED';
  fastChecksCompleted: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  hasMatches: boolean;
  paymentFlowStep: string | null;
  paidServices: {
    initialScreening: boolean;
    eddReport: boolean;
    matchReport: boolean;
    docRequest: boolean;
  };
  adverseMedia: {
    queued: boolean;
    queuedAt: string | null;
    startedAt: string | null;
    completedAt: string | null;
    error: string | null;
    retries: number;
  };
}

export async function getScreeningStatus(assessmentId: string): Promise<ScreeningStatusResponse> {
  return apiClient.fetch<ScreeningStatusResponse>(
    `/risk-assessments/${assessmentId}/screening-status`,
  );
}

export async function updatePaymentFlowStep(
  assessmentId: string,
  step: string | null,
): Promise<{ success: boolean }> {
  return apiClient.fetch<{ success: boolean }>(
    `/risk-assessments/${assessmentId}/payment-flow-step`,
    {
      method: 'PATCH',
      body: JSON.stringify({ step }),
    },
  );
}

export interface ClientRefSearchResult {
  clientRef: string;
  clientName: string | null;
}

export async function searchClientRefs(query?: string): Promise<ClientRefSearchResult[]> {
  const params = query ? `?q=${encodeURIComponent(query)}` : '';
  return apiClient.fetch<ClientRefSearchResult[]>(`/risk-assessments/search/client-refs${params}`);
}

// ============================================================================
// Audit Trail Types & Functions
// ============================================================================

/**
 * One item within an AI decision batch (per-result reasoning).
 */
export interface AiDecisionItem {
  target: string;
  targetLabel?: string;
  outcome: string;
  score?: number;
  confidence?: number;
  flags?: string[];
  reasoning: string;
}

/**
 * Structured AI reasoning attached to an audit entry that records a
 * Bedrock-driven decision batch.
 */
export interface AiDecision {
  decision: string;
  agent: string;
  model?: string;
  latencyMs?: number;
  tokenUsage?: { input: number; output: number };
  items: AiDecisionItem[];
  aggregate?: Record<string, unknown>;
}

/**
 * Individual audit trail entry
 */
export interface AuditTrailEntry {
  id: string;
  timestamp: string;
  agentName: string;
  message: string;
  sequenceNumber: number;
  category: string;
  eventType: string;
  /** Optional AI reasoning block — hidden in the modal unless user expands it. */
  aiDecision?: AiDecision;
}

/**
 * Audit trail response containing all entries for a case
 */
export interface AuditTrailResponse {
  caseId: string;
  clientRef: string;
  entries: AuditTrailEntry[];
}

export async function getAuditTrail(assessmentId: string): Promise<AuditTrailResponse> {
  return apiClient.fetch<AuditTrailResponse>(`/risk-assessments/${assessmentId}/audit-trail`);
}

export async function downloadAuditTrailPdf(assessmentId: string): Promise<void> {
  const { accessToken } = getAuthState();

  const headers: HeadersInit = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(`/api/risk-assessments/${assessmentId}/audit-trail/export`, {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    throw new Error('Failed to download audit trail PDF');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
  a.download = filenameMatch ? filenameMatch[1] : 'audit-trail.pdf';

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Query Keys
// ============================================================================

export const wizardKeys = {
  all: ['risk-assessment-wizard'] as const,
  clientDetails: (assessmentId: string) =>
    [...wizardKeys.all, 'client-details', assessmentId] as const,
  assessment: (assessmentId: string) => [...wizardKeys.all, 'assessment', assessmentId] as const,
  report: (assessmentId: string) => [...wizardKeys.all, 'report', assessmentId] as const,
  basicReport: (assessmentId: string) => [...wizardKeys.all, 'basic-report', assessmentId] as const,
  clientRefSearch: (query: string) => [...wizardKeys.all, 'client-ref-search', query] as const,
  screeningStatus: (assessmentId: string) =>
    [...wizardKeys.all, 'screening-status', assessmentId] as const,
  auditTrail: (assessmentId: string) => [...wizardKeys.all, 'audit-trail', assessmentId] as const,
};

// ============================================================================
// React Query Hooks
// ============================================================================

export function useClientDetails(assessmentId: string) {
  return useQuery({
    queryKey: wizardKeys.clientDetails(assessmentId),
    queryFn: () => getClientDetails(assessmentId),
    enabled: !!assessmentId,
  });
}

export function useWizardAssessment(assessmentId: string) {
  return useQuery({
    queryKey: wizardKeys.assessment(assessmentId),
    queryFn: () => getRiskAssessment(assessmentId),
    enabled: !!assessmentId,
  });
}

export function useClientRefSearch(query: string) {
  const result = useQuery({
    queryKey: wizardKeys.clientRefSearch(query),
    queryFn: () => searchClientRefs(query),
    enabled: query.length >= 2, // Require at least 2 characters to reduce API calls
    staleTime: 30000, // Cache for 30 seconds
  });

  // Use isFetching for loading state as isLoading is only true on first fetch
  return {
    ...result,
    isLoading: result.isFetching,
  };
}

/**
 * Hook for polling screening status during adverse media processing.
 * Uses progressive polling: starts fast (2s), then slows down (5s, 10s) over time.
 * Stops polling when status is COMPLETED or FAILED.
 *
 * @param assessmentId - The assessment ID to poll status for
 * @param enabled - Whether polling is enabled. Should be false in IDLE state to avoid
 *                  unnecessary API requests when user is just viewing the page.
 */
export function useScreeningStatus(assessmentId: string, enabled: boolean) {
  const pollCountRef = useRef(0);

  return useQuery({
    queryKey: wizardKeys.screeningStatus(assessmentId),
    queryFn: () => {
      pollCountRef.current += 1;
      return getScreeningStatus(assessmentId);
    },
    enabled: enabled && !!assessmentId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      // Stop polling when complete or failed
      if (status === 'COMPLETED' || status === 'FAILED') {
        pollCountRef.current = 0;
        return false;
      }

      // Progressive polling: fast at first, then slow down
      // First 5 polls: 2s, next 5: 5s, then 10s
      const pollCount = pollCountRef.current;
      if (pollCount <= 5) return 2000;
      if (pollCount <= 10) return 5000;
      return 10000; // Max 10s interval
    },
  });
}

export function useStartAssessment() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: startAssessment,
    onSuccess: (assessment) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      navigate({ to: '/risk-assessment/$id/client-details', params: { id: assessment.id } });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start assessment');
    },
  });
}

export function useSaveClientDetails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assessmentId, data }: { assessmentId: string; data: CreateClientDetailsDto }) =>
      saveClientDetails(assessmentId, data),
    onSuccess: (_, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: wizardKeys.clientDetails(assessmentId) });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      toast.success('Client details saved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save client details');
    },
  });
}

export function useSaveDraftClientDetails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assessmentId,
      data,
    }: {
      assessmentId: string;
      data: SaveDraftClientDetailsDto;
    }) => saveDraftClientDetails(assessmentId, data),
    onSuccess: (_, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: wizardKeys.clientDetails(assessmentId) });
      toast.success('Draft saved');
    },
    // Silent failure for drafts - just log
    onError: (error: Error) => {
      console.error('Failed to save draft:', error);
    },
  });
}

export function useRunScreening() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assessmentId,
      enabledChecks,
      adverseMediaMode = 'light',
    }: {
      assessmentId: string;
      enabledChecks?: string[];
      adverseMediaMode?: 'light' | 'dde';
    }) => runScreening(assessmentId, enabledChecks, adverseMediaMode),
    onSuccess: (_result, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      // Note: Navigation is handled by the ScreeningModal component
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to run screening');
    },
  });
}

export function useUpgradeToEdd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assessmentId,
      transactionId,
    }: {
      assessmentId: string;
      transactionId: string;
    }) => upgradeToEdd(assessmentId, transactionId),
    onSuccess: (_, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
      queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upgrade to EDD');
    },
  });
}

export function useAssessmentReport(assessmentId: string) {
  return useQuery({
    queryKey: wizardKeys.report(assessmentId),
    queryFn: () => getRiskAssessment(assessmentId),
    enabled: !!assessmentId,
  });
}

/**
 * Hook for fetching basic report data.
 * Used for users who have paid for initial screening but not the full report.
 */
export function useBasicReport(assessmentId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: wizardKeys.basicReport(assessmentId),
    queryFn: () => getBasicReport(assessmentId),
    enabled: enabled && !!assessmentId,
  });
}

/**
 * Hook for fetching audit trail data for a risk assessment.
 * Only fetches when modal is open (enabled = true).
 */
export function useAuditTrail(assessmentId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: wizardKeys.auditTrail(assessmentId),
    queryFn: () => getAuditTrail(assessmentId),
    enabled: enabled && !!assessmentId,
  });
}

export async function downloadEvidence(assessmentId: string, filename: string): Promise<void> {
  // Use /api prefix so Vite proxy routes this to the backend
  const { accessToken } = getAuthState();

  const headers: HeadersInit = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(
    `/api/risk-assessments/${assessmentId}/evidence/${encodeURIComponent(filename)}`,
    {
      credentials: 'include',
      headers,
    },
  );

  if (!response.ok) {
    throw new Error('Failed to download evidence file');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function downloadFileFromApi(
  path: string,
  defaultFilename: string,
  errorMessage: string,
): Promise<void> {
  const { accessToken } = getAuthState();

  const headers: HeadersInit = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(path, {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || errorMessage);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const contentDisposition = response.headers.get('Content-Disposition');
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
  a.download = filenameMatch ? filenameMatch[1] : defaultFilename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadAllEvidence(assessmentId: string): Promise<void> {
  return downloadFileFromApi(
    `/api/risk-assessments/${assessmentId}/evidence/download-all`,
    'evidence.zip',
    'Failed to download evidence files',
  );
}

export async function downloadBasicReportPdf(assessmentId: string): Promise<void> {
  return downloadFileFromApi(
    `/api/risk-assessments/${assessmentId}/basic-report/pdf`,
    'screening-summary.pdf',
    'Failed to download basic report PDF',
  );
}

export async function downloadFullReportPdf(assessmentId: string): Promise<void> {
  return downloadFileFromApi(
    `/api/risk-assessments/${assessmentId}/full-report/pdf`,
    'full-report.pdf',
    'Failed to download full report PDF',
  );
}

export async function downloadSraForm(assessmentId: string): Promise<void> {
  return downloadFileFromApi(
    `/api/risk-assessments/${assessmentId}/sra-form`,
    'SRA_Form.PDF',
    'Failed to download SRA form',
  );
}
